import { ArrowDown, ArrowUp, Download, ExternalLink, Share2 } from 'lucide-react';
import { Fragment } from 'react';
import type { ProfileRecord, SortDirection, SortKey } from '../types';
import { downloadCountForProfile, formatDownloads } from '../lib/downloadCounts';
import { absoluteProfileUrl, copyText, formatDate, packPath, profilePath } from '../lib/navigation';
import { downloadProfile } from '../lib/downloads';
import { CreatorLink } from './CreatorLink';
import { ConnectionBadge } from './ConnectionBadge';
import { Link } from './Link';
import { ReleaseStatus } from './ReleaseStatus';

interface ProfileTableProps {
  profiles: ProfileRecord[];
  selected?: Set<string>;
  onToggle?: (id: string) => void;
  onToggleAll?: () => void;
  sort?: SortKey;
  direction?: SortDirection;
  onSort?: (sort: SortKey) => void;
  notify: (message: string, tone?: 'success' | 'error') => void;
  compact?: boolean;
  showOutputResolution?: boolean;
  returnTo?: string;
  showReleaseStatus?: boolean;
}

const columns: Array<{ key: SortKey; label: string; className?: string; searchOnly?: boolean }> = [
  { key: 'name', label: 'Profile Name' },
  { key: 'device', label: 'Device', className: 'device-column optional-column' },
  { key: 'connection', label: 'Connection', className: 'connection-column' },
  { key: 'outputResolution', label: 'Output Res', className: 'resolution-column', searchOnly: true },
  { key: 'creator', label: 'Creator', className: 'creator-column optional-column' },
  { key: 'updatedAt', label: 'Updated', className: 'updated-column optional-column' },
  { key: 'downloads', label: 'Downloads', className: 'downloads-column' },
];

export function ProfileTable({
  profiles,
  selected,
  onToggle,
  onToggleAll,
  sort,
  direction,
  onSort,
  notify,
  compact = false,
  showOutputResolution = false,
  returnTo,
  showReleaseStatus = false,
}: ProfileTableProps) {
  const selectable = Boolean(selected && onToggle);
  const selectedIds = selected ?? new Set<string>();
  const toggleProfile = onToggle ?? (() => undefined);
  const allSelected = selectable && profiles.length > 0 && profiles.every((profile) => selectedIds.has(profile.id));
  const visibleColumns = columns.filter((column) => (!column.searchOnly || showOutputResolution) && (!showReleaseStatus || column.key !== 'updatedAt'));
  const shellClassName = ['table-shell', compact && 'compact-table', showOutputResolution && 'search-results-table'].filter(Boolean).join(' ');

  async function share(profile: ProfileRecord): Promise<void> {
    try {
      await copyText(absoluteProfileUrl(profile));
      notify('Profile link copied.');
    } catch {
      notify('Could not copy the link. Open the profile and copy its URL.', 'error');
    }
  }

  async function download(profile: ProfileRecord): Promise<void> {
    try {
      await downloadProfile(profile);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'The profile could not be downloaded.', 'error');
    }
  }

  function sortHeader(key: SortKey, label: string) {
    if (!onSort) return label;
    return (
      <button className="sort-button" type="button" onClick={() => onSort(key)}>
        {label}
        {sort === key && (direction === 'asc' ? <ArrowUp size={13} /> : <ArrowDown size={13} />)}
      </button>
    );
  }

  return (
    <div
      className={shellClassName}
      role="region"
      aria-label="Profile table; scroll horizontally to view all columns"
      tabIndex={0}
    >
      <table className="profile-table">
        <thead>
          <tr>
            {selectable && (
              <th className="check-column">
                <input type="checkbox" checked={allSelected} onChange={onToggleAll} aria-label="Select all profiles on this page" />
              </th>
            )}
            {visibleColumns.map((column, index) => (
              <Fragment key={column.key}>
                <th
                  className={column.className}
                  aria-sort={sort === column.key ? (direction === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {sortHeader(column.key, column.label)}
                </th>
                {index === 0 && <th className="profile-pack-cell optional-column">Profile Pack</th>}
              </Fragment>
            ))}
            <th className="actions-column"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {profiles.map((profile) => (
            <tr key={profile.id} className={selectedIds.has(profile.id) ? 'is-selected' : undefined}>
              {selectable && (
                <td className="check-column">
                  <input type="checkbox" checked={selectedIds.has(profile.id)} onChange={() => toggleProfile(profile.id)} aria-label={`Select ${profile.name}`} />
                </td>
              )}
              <td className="profile-name-cell">
                <Link href={profilePath(profile)} navigationState={returnTo ? { returnTo, fromSearch: true } : undefined}>{profile.name}</Link>
                {showReleaseStatus && (
                  <span className="profile-release">
                    <ReleaseStatus updated={Date.parse(profile.updatedAt) > Date.parse(profile.uploadedAt)} />
                    <time dateTime={profile.updatedAt}>{formatDate(profile.updatedAt)}</time>
                  </span>
                )}
              </td>
              <td className="profile-pack-cell optional-column">
                {profile.packName
                  ? profile.packId
                    ? <Link href={packPath({ id: profile.packId, name: profile.packName })}>{profile.packName}</Link>
                    : profile.packName
                  : <span className="empty-table-value" aria-label="No profile pack">—</span>}
              </td>
              <td className="device-column optional-column">{profile.device}</td>
              <td className="connection-column"><ConnectionBadge connection={profile.connection} /></td>
              {showOutputResolution && <td className="resolution-column">{profile.outputResolution}</td>}
              <td className="creator-column optional-column"><CreatorLink name={profile.creator} /></td>
              {!showReleaseStatus && <td className="updated-column optional-column"><time dateTime={profile.updatedAt}>{formatDate(profile.updatedAt)}</time></td>}
              <td className="number-cell downloads-column">{formatDownloads(downloadCountForProfile(profile))}</td>
              <td className="actions-column">
                <div className="row-actions">
                  <Link className="icon-button subtle" href={profilePath(profile)} navigationState={returnTo ? { returnTo, fromSearch: true } : undefined} aria-label={`View ${profile.name}`} title="View details"><ExternalLink size={16} /></Link>
                  <button className="icon-button subtle" type="button" onClick={() => void share(profile)} aria-label={`Share ${profile.name}`} title="Copy profile link"><Share2 size={16} /></button>
                  <button className="icon-button subtle download-action" type="button" onClick={() => void download(profile)} aria-label={`Download ${profile.name}`} title="Download profile"><Download size={16} /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
